import { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup } from "react-leaflet";
import { Link } from "react-router";
import {
  Body,
  Caption,
  ErrorMessage,
  LoadingState,
  PageHeader,
  Card,
} from "@/components/ui";
import "leaflet/dist/leaflet.css";
import { useAssets } from "@/domains/assets/api/queries";
import type { AssetStatus, AssetType } from "@/domains/assets/api/types";
import {
  AssetTypeIcon,
  assetTypeLabel,
} from "@/domains/assets/components/assetTypeLabel";
import { AssetStatusBadge } from "@/domains/assets/components/assetStatusBadge";

const ANTWERPEN_CENTER: [number, number] = [51.2194, 4.4025];

const STATUS_COLOR: Record<AssetStatus, string> = {
  available: "#10b981",
  inUse: "#2563eb",
  maintenance: "#f59e0b",
  outOfService: "#dc2626",
};

const LEGEND: ReadonlyArray<{ status: AssetStatus; label: string }> = [
  { status: "inUse", label: "In gebruik op locatie" },
  { status: "available", label: "Beschikbaar (depot)" },
  { status: "maintenance", label: "Onderhoud" },
  { status: "outOfService", label: "Buiten werking" },
];

const TYPES_IN_ORDER: ReadonlyArray<AssetType> = [
  "camera",
  "mobileTree",
  "camcar",
  "batteryPack",
  "trailer",
];

export function AssetMapPage(): React.JSX.Element {
  const { data, isPending, isError } = useAssets();

  const markers = useMemo(() => {
    const list = data ?? [];
    return list.map((a) => {
      const loc = a.currentLocation ?? a.homeBase;
      return { asset: a, lat: loc.lat, lng: loc.lng };
    });
  }, [data]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Asset kaart"
        subtitle="Real-time locatie van alle mobiele camera's, trees, camcars en trailers."
      />

      {isPending ? (
        <LoadingState label="Kaart laden…" />
      ) : isError ? (
        <ErrorMessage message="Kon de asset locaties niet ophalen." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr,16rem]">
          <Card padding="none" className="overflow-hidden">
            <MapContainer
              center={ANTWERPEN_CENTER}
              zoom={12}
              scrollWheelZoom
              className="h-[640px] w-full"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {markers.map(({ asset, lat, lng }) => (
                <CircleMarker
                  key={asset.id}
                  center={[lat, lng]}
                  radius={9}
                  pathOptions={{
                    color: STATUS_COLOR[asset.status],
                    fillColor: STATUS_COLOR[asset.status],
                    fillOpacity: 0.7,
                    weight: 2,
                  }}
                >
                  <Tooltip direction="top" offset={[0, -6]}>
                    <span className="font-semibold">{asset.number}</span> · {assetTypeLabel(asset.type)}
                  </Tooltip>
                  <Popup>
                    <div className="space-y-1 text-sm">
                      <div className="font-semibold text-foreground">
                        {asset.number} — {assetTypeLabel(asset.type)}
                      </div>
                      <div className="text-muted-foreground">{asset.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {(asset.currentLocation ?? asset.homeBase).address}
                      </div>
                      <div className="pt-1">
                        <AssetStatusBadge status={asset.status} />
                      </div>
                      <Link
                        to={`/assets/${asset.id}`}
                        className="block pt-1 text-xs font-medium text-primary-700 hover:underline"
                      >
                        Detail openen →
                      </Link>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </Card>

          <div className="space-y-4">
            <Card padding="md">
              <Body weight="semibold" className="mb-2">
                Legende
              </Body>
              <ul className="space-y-2">
                {LEGEND.map(({ status, label }) => (
                  <li key={status} className="flex items-center gap-2 text-sm">
                    <span
                      className="size-3 rounded-full"
                      style={{ backgroundColor: STATUS_COLOR[status] }}
                      aria-hidden
                    />
                    {label}
                  </li>
                ))}
              </ul>
            </Card>
            <Card padding="md">
              <Body weight="semibold" className="mb-2">
                Per type
              </Body>
              <ul className="space-y-2 text-sm">
                {TYPES_IN_ORDER.map((t) => {
                  const count = (data ?? []).filter((a) => a.type === t).length;
                  return (
                    <li key={t} className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <AssetTypeIcon type={t} className="size-4 text-muted-foreground" />
                        {assetTypeLabel(t)}
                      </span>
                      <Caption tone="default">{count}</Caption>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
